import { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isLeft } from "fp-ts/lib/Either";
import LocalizationContext from "../../../providers/localization/LocalizationContext";
import ModalsContext from "../../../providers/modal/ModalsContext";
import KeyWordLocalization from "../../../utils/KeyWordLocalization";
import routes from "../../../routes/Routes";
import SearchResultEntity from "../../../../domain/entities/SearchResultEntity";
import ShiftEntity, { ShiftStatus } from "../../../../domain/entities/ShiftEntity";
import di from "../../../../DependencyInjection";
import SearchShiftsUseCase, { SearchShiftsUseCaseName } from "../../../../domain/use_cases/shift/SearchShiftsUseCase";
import DeleteShiftUseCase, { DeleteShiftUseCaseName } from "../../../../domain/use_cases/shift/DeleteShiftUseCase";
import ReasignShiftUseCase, { ReasignShiftUseCaseName } from "../../../../domain/use_cases/shift/ReasignShiftUseCase";
import CreateShiftComponent from "./form/create_shift.component";
import ReasignShiftModal from "./reasign/ReasignShiftModal";
import DominicanTime from "../../../../data/dto/DominicanTime";

const useShiftListViewModel = () => {
    const [searchParams] = useSearchParams();
    const searchQuery = searchParams.get("search");
    const pageQuery = parseInt(searchParams.get("page") ?? "1");

    const { i18n } = useContext(LocalizationContext);
    const { addToast, openModalDelete, openModalCustom, closeModalCustom, closeModalDelete } = useContext(ModalsContext);
    const { register, handleSubmit, getValues, setValue, watch, formState: { errors } } = useForm();
    const [result, setResult] = useState<SearchResultEntity<ShiftEntity> | undefined>(undefined);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const navigate = useNavigate();
    const itemsPerPage = 20;
    let _timerTap: any;

    const _searchShifts = async () => {
        const data = getValues();
        clearTimeout(_timerTap);
        setResult(undefined);
        const response = await di.get<SearchShiftsUseCase>(SearchShiftsUseCaseName).call(
            data["query"],
            data["currentPage"] || 1,
            itemsPerPage
        );
        if (isLeft(response)) {
            setResult({
                data: [],
                page: 1,
                total: 0,
                itemsPerPage: 0,
                pages: 0,
            });
            return;
        }
        setResult(response.right);
    };

    const _handleSearch = (data: any) => {
        const search = data["query"];
        const page = data["currentPage"];
        let query = "?";
        if (search && search.length > 0) query += `search=${search}&`;
        if (page) query += `page=${page}&`;
        if (query.endsWith("&")) query = query.slice(0, -1);
        navigate(routes.shift_list.relativePath + query);
    };

    const _handleChangeTextSearch = () => {
        clearTimeout(_timerTap);
        const data = getValues();
        _timerTap = setTimeout(() => _handleSearch(data), 500);
    };

    const _handleChange = () => {
        const data = getValues();
        _handleSearch(data);
    };

    const _handleLoad = () => {
        setValue("query", searchQuery);
        setValue("currentPage", pageQuery);
        _searchShifts();
    };

    const _handleOnChangePage = (page: number) => {
        setValue("currentPage", page);
        const data = getValues();
        _handleSearch(data);
    };

    const _handleOnClickDeleteShift = async (shift: ShiftEntity) => {
        const deleteShift = async () => {
            const response = await di.get<DeleteShiftUseCase>(DeleteShiftUseCaseName).call(shift.id);
            if (isLeft(response)) return addToast(response.left.code ?? KeyWordLocalization.UnknownError, 'error', undefined);
            closeModalDelete();
            addToast(i18n("Turno eliminado con éxito"), 'success', undefined);
            _searchShifts();
        };
        openModalDelete(
            i18n("Eliminar Turno"),
            i18n("¿Estás seguro de que deseas eliminar este turno?"),
            deleteShift
        );
    };

    const _handleOnClickUpdateShift = async (shift: ShiftEntity) => {
        const onUpdated = (updatedShift: ShiftEntity) => {
            closeModalCustom();
            _searchShifts();
        };
        openModalCustom(
            "lg",
            i18n("Editar Turno"),
            <CreateShiftComponent onUpdated={onUpdated} shift={shift} />
        );
    };

    const _handleOnCreateSingleShift = async () => {
        const onCreated = (createdShift: ShiftEntity) => {
            closeModalCustom();
            _searchShifts();
        };
        openModalCustom(
            "lg",
            i18n("Crear Turno"),
            <CreateShiftComponent onCreated={onCreated} />
        );
    };

    const _handleOnReasignShift = async (shift: ShiftEntity) => {
        const onReasign = (reasignedShift: ShiftEntity) => {
            closeModalCustom();
            _searchShifts();
        };
        openModalCustom(
            "lg",
            i18n("Reasignar Turno"),
            <ReasignShiftModal 
                shift={shift} 
                onReasign={onReasign} 
                onClose={closeModalCustom}
            />
        );
    };

    const _canReasignShift = (shift: ShiftEntity): boolean => {
        const now = DominicanTime.nowDominicanTime();
        console.log("can reasign?", {
            "shift name": shift.id,
            "shift status": shift.status,
            "now": now,
            "shift end date": shift.endDate,
            "stablishment": shift.stablishment.name,
            "valet": shift.user.name,
        });
        return (shift.status === ShiftStatus.REJECTED || shift.status === ShiftStatus.NO_CONFIRMED || shift.status == ShiftStatus.PENDING) && now < shift.endDate && ([ShiftStatus.FINISHED, ShiftStatus.STARTED].includes(shift.status) === false);
    };

    useEffect(() => {
        _handleLoad();
    }, [searchQuery, pageQuery]);

    return {
        formInfo: { register, handleSubmit, getValues, setValue, watch, errors },
        result,
        loadingDownload,
        i18n,
        _handleSearch,
        _handleChangeTextSearch,
        _handleChange,
        _handleOnChangePage,
        _handleOnClickDeleteShift,
        _handleOnClickUpdateShift,
        _handleOnCreateSingleShift,
        _handleOnReasignShift,
        _canReasignShift,
    };
};

export default useShiftListViewModel;